import { motion } from "framer-motion";
import { ArrowUpRight, Radar, Clock, Link2 } from "lucide-react";
import { PILLARS } from "@/lib/brand";

const StatePill = ({ state }) => {
  if (state === "active")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 px-3 py-1 text-xs font-semibold">
        <Radar className="h-3.5 w-3.5" /> Live Engine
      </span>
    );
  if (state === "connected")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 text-sky-800 border border-sky-500/30 px-3 py-1 text-xs font-semibold">
        <Link2 className="h-3.5 w-3.5" /> NextGen Connected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/30 px-3 py-1 text-xs font-semibold">
      <Clock className="h-3.5 w-3.5" /> Launching Soon
    </span>
  );
};

export const PillarsGrid = ({ onSport, onLaunching, onNextGen }) => {
  return (
    <section id="pillars" className="relative bg-talanta-cream py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">Six Pillars, One ID</span>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-talanta-green">
            Every path to turn talent into livelihood
          </h2>
          <p className="mt-4 text-talanta-charcoal/70">
            Enter through any pillar with the same One Talanta ID. Talanta Sport, Soko and Broadcast run on the NextGen tournament engine — the rest are launching soon.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            const isActive = p.state === "active";
            const isConnected = p.state === "connected";
            return (
              <motion.button
                key={p.id}
                data-testid={`pillar-card-${p.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                onClick={() => (isActive ? onSport() : isConnected ? onNextGen(p) : onLaunching(p))}
                className="group text-left rounded-2xl overflow-hidden bg-white border border-talanta-green/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-talanta-green/80 via-talanta-green/10 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <StatePill state={p.state} />
                  </div>
                  <div className="absolute bottom-3 left-3 h-11 w-11 rounded-xl bg-white/95 flex items-center justify-center shadow-lg">
                    <Icon className={`h-5 w-5 ${isActive ? "text-talanta-red" : isConnected ? "text-talanta-green" : "text-talanta-terracotta"}`} />
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-xl text-talanta-green">{p.name}</h3>
                    <ArrowUpRight className="h-5 w-5 text-talanta-green/30 group-hover:text-talanta-red group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <p className="mt-2 text-sm text-talanta-charcoal/65 leading-relaxed">{p.blurb}</p>
                  <span className={`mt-4 inline-block text-sm font-semibold ${isActive ? "text-talanta-red" : isConnected ? "text-talanta-green" : "text-talanta-terracotta"}`}>
                    {isActive ? "Connect to live engine →" : isConnected ? (p.id === "soko" ? "Browse the marketplace →" : "Watch tournaments →") : "Get launch access →"}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
